import { DependencyList, useRef, useCallback, useEffect } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { Keys, Options } from 'react-hotkeys-hook/dist/types'
import { isPhysicalArrowKey } from 'src/hooks/sceneArrowKeys'

/** Keep browser history (Ctrl+Left/Right) from firing, but leave plain arrows to widgets. */
const preventDefaultUnlessPhysicalArrow = (event: KeyboardEvent) =>
  !isPhysicalArrowKey(event) || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey

// https://app.clickup.com/t/2590559/MDG-3064
// https://www.toptal.com/developers/keycode
const eventCodeToStoreyMap: { [key: string]: string } = {
  Digit0: '0',
  Digit1: '1',
  Digit2: '2',
  Digit3: '3',
  Digit4: '4',
  Digit5: '5',
  Digit6: '6',
  Digit7: '7',
  Digit8: '8',
  Digit9: '9',
  Numpad0: '0',
  Numpad1: '1',
  Numpad2: '2',
  Numpad3: '3',
  Numpad4: '4',
  Numpad5: '5',
  Numpad6: '6',
  Numpad7: '7',
  Numpad8: '8',
  Numpad9: '9',
  Insert: '0',
  End: '1',
  ArrowDown: '2',
  PageDown: '3',
  ArrowLeft: '4',
  Clear: '5',
  ArrowRight: '6',
  Home: '7',
  ArrowUp: '8',
  PageUp: '9',
}

export const useHotkeys_Ctrl_0to9 = (
  callback: (storeyId: string) => void,
  options: Options = {},
  dependencies: DependencyList = [],
) => {
  return useHotkeys(
    [
      // these keys are used for digit keys and when numlock is on
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      // adding ctrl modifiers
      'CTRL+0',
      'CTRL+1',
      'CTRL+2',
      'CTRL+3',
      'CTRL+4',
      'CTRL+5',
      'CTRL+6',
      'CTRL+7',
      'CTRL+8',
      'CTRL+9',
      // numpad keys when numlock is off
      'Insert',
      'End',
      'PageDown',
      'ArrowLeft',
      'Clear',
      'ArrowRight',
      'Home',
      'PageUp',
      // adding ctrl modifiers
      'CTRL+Insert',
      'CTRL+End',
      'CTRL+ArrowDown',
      'CTRL+PageDown',
      'CTRL+ArrowLeft',
      'CTRL+Clear',
      'CTRL+ArrowRight',
      'CTRL+Home',
      'CTRL+ArrowUp',
      'CTRL+PageUp',
    ],
    event => {
      // Physical arrows pan the floorplan. Numpad arrows (numlock off) still
      // report Arrow* as `key` but Numpad* as `code`, so they keep selecting storeys.
      if (event.ctrlKey && !isPhysicalArrowKey(event)) {
        const storeyId = eventCodeToStoreyMap[event.code]
        if (storeyId) {
          callback(storeyId)
        }
      }
    },
    { preventDefault: preventDefaultUnlessPhysicalArrow, ...options },
    dependencies,
  )
}

export const useHotkeys_0to9WithNumpad = (
  callback: (storeyId: string) => void,
  options: Options = {},
  dependencies: DependencyList = [],
) => {
  return useHotkeys(
    [
      // these keys are used for digit keys and when numlock is on
      '0',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      // numpad keys when numlock is off
      'Insert',
      'End',
      'PageDown',
      'ArrowLeft',
      'Clear',
      'ArrowRight',
      'Home',
      'PageUp',
    ],
    event => {
      if (!event.ctrlKey && !isPhysicalArrowKey(event)) {
        const storeyId = eventCodeToStoreyMap[event.code]
        if (storeyId) {
          callback(storeyId)
        }
      }
    },
    { preventDefault: preventDefaultUnlessPhysicalArrow, ...options },
    dependencies,
  )
}

export const useHotkeys_0to9WithNumpadAndLongPress = (
  singlePressCallback: (storeyId: string) => void,
  longPressCallback: (storeyId: string) => void,
  options: Options = {},
  dependencies: DependencyList = [],
) => {
  const keys = [
    // these keys are used for digit keys and when numlock is on
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    // numpad keys when numlock is off
    'Insert',
    'End',
    'PageDown',
    'ArrowLeft',
    'Clear',
    'ArrowRight',
    'Home',
    'PageUp',
  ]

  const handleSinglePress = useCallback(
    (event: KeyboardEvent) => {
      if (event.ctrlKey || isPhysicalArrowKey(event)) return
      const storeyId = eventCodeToStoreyMap[event.code]
      if (storeyId) {
        singlePressCallback(storeyId)
      }
    },
    [singlePressCallback],
  )

  const handleLongPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.ctrlKey || isPhysicalArrowKey(event)) return
      const storeyId = eventCodeToStoreyMap[event.code]
      if (storeyId) {
        longPressCallback(storeyId)
      }
    },
    [longPressCallback],
  )

  return useHotkeys_ShortAndLongPress(
    keys,
    handleSinglePress,
    handleLongPress,
    options,
    dependencies,
  )
}

export const useHotkeys_ShortAndLongPress = (
  keys: Keys,
  singlePressCallback: (event: KeyboardEvent) => void,
  longPressCallback: (event: KeyboardEvent) => void,
  options: Options = {},
  dependencies: DependencyList = [],
) => {
  const pressStartTime = useRef<number | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isKeyPressed = useRef<boolean>(false)
  const longPressDelay = 250

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Only handle if not already pressed
      if (!isKeyPressed.current) {
        isKeyPressed.current = true
        pressStartTime.current = Date.now()

        // Set timeout for long press
        timeoutRef.current = setTimeout(() => {
          if (isKeyPressed.current && pressStartTime.current) {
            const pressDuration = Date.now() - pressStartTime.current
            if (pressDuration >= longPressDelay) {
              longPressCallback(event)
            }
          }
        }, longPressDelay)
      }
    },
    [longPressCallback, longPressDelay],
  )

  const handleKeyUp = useCallback(
    (event: KeyboardEvent) => {
      // Clear timeout and reset state when key is released
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // If key was released before long press delay, trigger single press
      if (isKeyPressed.current && pressStartTime.current) {
        const pressDuration = Date.now() - pressStartTime.current
        if (pressDuration < longPressDelay) {
          singlePressCallback(event)
        }
      }

      isKeyPressed.current = false
      pressStartTime.current = null
    },
    [singlePressCallback, longPressDelay],
  )

  // Use react-hotkeys-hook for key detection
  useHotkeys(
    keys,
    handleKeyDown,
    {
      keydown: true,
      keyup: false,
      preventDefault: preventDefaultUnlessPhysicalArrow,
      ...options,
    },
    dependencies,
  )

  // Add keyup listener manually
  useEffect(() => {
    const handleKeyUpEvent = (event: KeyboardEvent) => {
      handleKeyUp(event)
    }

    document.addEventListener('keyup', handleKeyUpEvent)
    return () => {
      document.removeEventListener('keyup', handleKeyUpEvent)
    }
  }, [handleKeyUp])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])
}
